import React, { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { LanguageProvider } from './i18n.js';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import SocialAuthCallback from './components/SocialAuthCallback';
import LandingPage from './components/LandingPage';
import AuthPage from './components/AuthPage';
import LegalPages from './components/LegalPages';
import AboutPage from './components/AboutPage';
import ProfileSetup from './components/ProfileSetup';
import Dashboard from './components/Dashboard';
import NativeProvider from './components/NativeProvider';
import OfflineNotice from './components/OfflineNotice';
import EmailVerificationPage from './components/EmailVerificationPage';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Suppress ResizeObserver loop errors (common on Android with Recharts)
if (typeof window !== 'undefined') {
  const origError = window.onerror;
  window.onerror = function(msg) {
    if (typeof msg === 'string' && msg.includes('ResizeObserver')) return true;
    if (origError) return origError.apply(this, arguments);
    return false;
  };
  window.addEventListener('error', (e) => {
    if (e.message?.includes?.('ResizeObserver')) {
      e.stopImmediatePropagation();
    }
  });
  // Suppress unhandled promise rejections that crash Android
  window.addEventListener('unhandledrejection', (e) => {
    console.warn('Unhandled rejection:', e.reason);
    e.preventDefault();
  });
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [authView, setAuthView] = useState('landing');
  const [resetToken, setResetToken] = useState(null);
  const [verifyToken, setVerifyToken] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  const { user, loading, login, fetchUser } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetTkn = urlParams.get('reset_token');
    const verifyTkn = urlParams.get('verify_token');
    
    if (resetTkn) {
      setResetToken(resetTkn);
      setAuthView('reset-password');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (verifyTkn) {
      setVerifyToken(verifyTkn);
      setAuthView('verify-email');
      window.history.replaceState({}, document.title, window.location.pathname);
      verifyEmail(verifyTkn);
    }
    
    const path = window.location.pathname;
    const hash = window.location.hash;
    if (hash.includes('session_id=') || path.startsWith('/auth/callback')) {
      setAuthView('social-callback');
    }
    if (path.startsWith('/settings/link/')) {
      setAuthView('link-callback');
    }
  }, []);

  const handleLinkCallback = async () => {
    const hash = window.location.hash;
    const path = window.location.pathname;
    
    if (hash.includes('session_id=') && path.includes('/settings/link/google')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/auth/link/google`, 
            { session_id: sessionId },
            { headers: { Authorization: `Bearer ${token}` }}
          );
          window.history.replaceState({}, document.title, '/');
          window.location.reload();
        } catch (err) {
          console.error('Link error:', err);
          alert(err.response?.data?.detail || 'Failed to link Google account');
          window.history.replaceState({}, document.title, '/');
          window.location.reload();
        }
      }
    }
  };

  useEffect(() => {
    if (authView === 'link-callback') {
      handleLinkCallback();
    }
  }, [authView]);

  const verifyEmail = async (token) => {
    setVerificationStatus('verifying');
    try {
      const response = await axios.post(`${API}/auth/verify-email?token=${token}`);
      setVerificationStatus('success');
      setVerificationMessage(response.data.message);
      if (user) fetchUser();
    } catch (err) {
      setVerificationStatus('error');
      setVerificationMessage(err.response?.data?.detail || 'Verification failed. The link may have expired.');
    }
  };

  const handleAuthSuccess = (token, userData) => {
    // Clear any visitor-timeout lock — the user is signing in.
    try { localStorage.removeItem('visitor_timeout_triggered'); } catch {}
    document.body.style.overflow = '';
    // IMPORTANT: reset authView so the social-callback screen unmounts
    // and the app re-renders with the authenticated user state.
    setAuthView('landing');
    login(token, userData);
  };
  const handleGetStarted = () => setAuthView('register');
  const handleLogin = () => setAuthView('login');
  const handleBackToLanding = () => {
    setAuthView('landing');
    setResetToken(null);
    setVerifyToken(null);
    setVerificationStatus(null);
  };

  // Social auth callback
  if (authView === 'social-callback') {
    return (
      <SocialAuthCallback 
        onSuccess={handleAuthSuccess}
        onError={() => setAuthView('login')}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
        <div className="text-center">
          <img src="/logo-v4.png" alt="Logo" className="w-24 h-24 object-contain mx-auto mb-6 animate-pulse" />
          <div className="text-lg text-gray-600 font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    // Email verification result page
    if (authView === 'verify-email') {
      return (
        <EmailVerificationPage
          status={verificationStatus}
          message={verificationMessage}
          onLogin={() => setAuthView('login')}
          onBack={handleBackToLanding}
        />
      );
    }

    if (['privacy', 'terms', 'privacy-policy', 'terms-of-service', 'health-disclaimer', 'ai-disclaimer'].includes(authView)) {
      return <LegalPages page={authView} onBack={handleBackToLanding} />;
    }

    if (authView === 'about') {
      return <AboutPage onBack={handleBackToLanding} />;
    }
    
    if (authView === 'landing') {
      return (
        <LandingPage 
          onGetStarted={handleGetStarted} 
          onLogin={handleLogin}
          onPrivacy={() => setAuthView('privacy-policy')}
          onTerms={() => setAuthView('terms-of-service')}
          onAbout={() => setAuthView('about')}
          onLegalPage={(page) => setAuthView(page)}
        />
      );
    }
    
    return (
      <AuthPage 
        mode={authView === 'reset-password' ? 'login' : authView}
        onAuthSuccess={handleAuthSuccess}
        onBack={handleBackToLanding}
        onSwitchMode={(mode) => setAuthView(mode)}
        resetToken={resetToken}
      />
    );
  }

  if (!user?.profile) {
    return <ProfileSetup onComplete={() => window.location.reload()} onLogout={() => {
      localStorage.removeItem('token');
      window.location.reload();
    }} />;
  }

  return <DashboardWithTerms />;
}

// Wrap Dashboard with Terms Acceptance check.
// Shows the legal disclaimer modal if user hasn't accepted the latest terms version.
function DashboardWithTerms() {
  const [needsTerms, setNeedsTerms] = useState(null); // null = loading, true = show modal, false = show app

  useEffect(() => {
    const checkTerms = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/legal/terms/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNeedsTerms(!!res.data?.needs_acceptance);
      } catch (err) {
        console.error('Terms status check failed:', err);
        setNeedsTerms(false); // fail-open so the app isn't blocked on a transient error
      }
    };
    checkTerms();
  }, []);

  if (needsTerms === null) {
    return null; // brief blank moment while we check
  }

  return (
    <>
      <Dashboard />
      {needsTerms && (
        <TermsAcceptanceModal
          onAccept={() => setNeedsTerms(false)}
          onLogout={() => {
            localStorage.removeItem('token');
            window.location.reload();
          }}
        />
      )}
    </>
  );
}

function AppWrapper() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <NativeProvider>
              <OfflineNotice />
              <App />
              <PWAInstallPrompt />
            </NativeProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default AppWrapper;
